import React, { useMemo } from 'react';
import { faRefresh, faAdd, faExchangeAlt, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import ActionToolbar from '../common/ActionToolbar';
import { useTranslation } from 'react-i18next';

/**
 * WarehouseInventoryActions
 * Props:
 * - searchValue, onSearchChange
 * - warehouseFilterValue, onWarehouseFilterChange
 * - lowStockFilterValue, onLowStockFilterChange (values: 'all'|'yes'|'no')
 * - onRefresh, onAddInventory, onTransferStock
 * - loading, refreshLoading, showSearch
 */
const WarehouseInventoryActions = ({
  searchValue = '',
  onSearchChange,
  warehouseFilterValue = '',
  onWarehouseFilterChange,
  lowStockFilterValue = 'all',
  onLowStockFilterChange,
  subCategoryFilterValue = [], // array of catalogue ids; [] = all
  onSubCategoryFilterChange,
  subCategoryOptions = [], // [{ value, label }]
  subCategoriesLoading = false,
  onRefresh,
  onAddInventory,
  onTransferStock,
  onExportBySubcategory, // Super Admin only — omitted (undefined) for other roles
  exportLoading = false,
  loading = false,
  refreshLoading = false,
  showSearch = true,
  warehouseOptions = [], // [{ value, label }]
  // Optional: pass the local warehouse inventory rows so we can derive warehouse names if options are not provided
  warehouseInventory = [], // [{ warehouse_id, warehouse_name, ... }]
}) => {
  const { t } = useTranslation();
  const actions = useMemo(() => {
    const list = [
      {
        key: 'refresh-inventory',
        label: t('warehouseInventory.actions.buttons.refresh'),
        icon: faRefresh,
        onClick: onRefresh,
        disabled: refreshLoading || loading,
        variant: 'ghost',
        showLabelBreakpoint: 'sm',
        spinner: refreshLoading,
      },
    ];

    // Export by Sub Category — only when a handler is provided (Super Admin gated by parent)
    if (onExportBySubcategory) {
      list.push({
        key: 'export-by-subcategory',
        label: t('warehouseInventory.actions.buttons.exportBySubcategory'),
        icon: faFileCsv,
        onClick: onExportBySubcategory,
        disabled: loading || refreshLoading || exportLoading,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
        spinner: exportLoading,
      });
    }

    // Write actions — only when a handler is provided (hidden for read-only
    // roles that have VIEW_INVENTORY but not MANAGE_INVENTORY).
    if (onTransferStock) {
      list.push({
        key: 'transfer-stock',
        label: t('warehouseInventory.actions.buttons.transferStock'),
        icon: faExchangeAlt,
        onClick: onTransferStock,
        disabled: loading || refreshLoading,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
      });
    }
    if (onAddInventory) {
      list.push({
        key: 'add-inventory',
        label: t('warehouseInventory.actions.buttons.add'),
        icon: faAdd,
        onClick: onAddInventory,
        disabled: loading || refreshLoading,
        variant: 'primary',
        showLabelBreakpoint: 'sm',
      });
    }

    return list;
  }, [onAddInventory, onRefresh, onTransferStock, onExportBySubcategory, exportLoading, loading, refreshLoading]);

  const filters = useMemo(() => {
    // Build warehouse options: prefer provided `warehouseOptions`; if empty, derive from `warehouseInventory` rows
    const derivedWarehouseOptions = (Array.isArray(warehouseOptions) && warehouseOptions.length > 0)
      ? warehouseOptions
      : (() => {
        // Collect warehouses from nested `warehouse_breakdown` arrays where available,
        // otherwise fall back to top-level warehouse fields on the row.
        const map = new Map();
        (warehouseInventory || []).forEach(row => {
          // If this row contains a warehouse_breakdown array, extract each entry
          if (Array.isArray(row.warehouse_breakdown) && row.warehouse_breakdown.length) {
            row.warehouse_breakdown.forEach(w => {
              const id = w?.warehouse_id ?? w?.id ?? null;
              const name = w?.warehouse_name ?? w?.warehouseName ?? w?.name ?? null;
              if (name && !map.has(name)) {
                map.set(name, { value: id != null ? String(id) : name, label: name });
              }
            });
            return;
          }

          // Fallback: top-level warehouse fields on the row
          const id = row?.warehouse_id ?? row?.warehouseId ?? row?.warehouse?.id ?? null;
          const name = row?.warehouse_name ?? row?.warehouse?.name ?? row?.warehouseName ?? null;
          if (name && !map.has(name)) {
            map.set(name, { value: id != null ? String(id) : name, label: name });
          }
        });
        return Array.from(map.values());
      })();

    return [
      {
        key: 'filter-subcategory',
        type: 'multiselect',
        label: t('warehouseInventory.actions.filters.subCategory'),
        value: subCategoryFilterValue,
        options: subCategoryOptions,
        onChange: onSubCategoryFilterChange,
        placeholder: t('warehouseInventory.actions.filters.subCategoryAll'),
        loading: subCategoriesLoading,
        minWidth: 220,
      },
      {
        key: 'filter-warehouse',
        type: 'select',
        label: t('warehouseInventory.actions.filters.warehouseName'),
        value: warehouseFilterValue,
        options: [{ value: '', label: t('warehouseInventory.actions.filters.all') }, ...derivedWarehouseOptions],
        onChange: onWarehouseFilterChange,
      },
      {
        key: 'filter-lowstock',
        type: 'select',
        label: t('warehouseInventory.actions.filters.lowStock'),
        value: lowStockFilterValue,
        options: [
          { value: 'all', label: t('warehouseInventory.actions.filters.lowStockOptions.all') },
          { value: 'yes', label: t('warehouseInventory.actions.filters.lowStockOptions.yes') },
          { value: 'no', label: t('warehouseInventory.actions.filters.lowStockOptions.no') },
        ],
        onChange: onLowStockFilterChange,
      },
    ];
  }, [warehouseFilterValue, lowStockFilterValue, onWarehouseFilterChange, onLowStockFilterChange, warehouseOptions, warehouseInventory, subCategoryFilterValue, subCategoryOptions, subCategoriesLoading, onSubCategoryFilterChange]);

  return (
    <ActionToolbar
      showSearch={showSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('warehouseInventory.actions.searchPlaceholder')}
      filters={filters}
      actions={actions}
    />
  );
};

export default WarehouseInventoryActions;
